import React, { useState, useMemo } from 'react';
import { Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';

import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import { getFirestore, collection, getDocs, doc, setDoc, deleteDoc } from 'firebase/firestore';
import './Dashboard.css';
import * as XLSX from 'xlsx';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import logo from './Logo_White.jpg'; // Import the logo

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const Dashboard = () => {
  const [customerData, setCustomerData] = useState([]);
  const [staffData, setStaffData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dashboardAuthenticated, setDashboardAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [authError, setAuthError] = useState('');
    const [modalUser, setModalUser] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showCustomerTable, setShowCustomerTable] = useState(false);
  const [showStaffTable, setShowStaffTable] = useState(false);
  const [blacklistData, setBlacklistData] = useState([]);
  const [showBlacklistTable, setShowBlacklistTable] = useState(false);

  const auth = getAuth();
  const db = getFirestore();

  const handleSaveCustomerInfo = async (phoneNumber, name, type) => {
    // NOTE: This function only updates the component's state.
    // You will need to add the logic to save this data to Firestore.
    console.log(`Updating ${phoneNumber}: Name - ${name}, Type - ${type}`);

    if (type === 'Staff') {
        const userToMove = customerData.find(u => u.phoneNumber === phoneNumber);
        if (userToMove) {
            setCustomerData(customerData.filter(u => u.phoneNumber !== phoneNumber));
            setStaffData([...staffData, { ...userToMove, name, type }]);
            // You might want to update the staffPhoneNumbers map as well
        }
    } else {
        setCustomerData(customerData.map(user => 
            user.phoneNumber === phoneNumber ? { ...user, name, type } : user
        ));
    }

    setModalUser(null);
  };

  const handleBlacklistUser = async (user) => {
    if (window.confirm(`Are you sure you want to blacklist ${user.phoneNumber}? This can be undone.`)) {
      try {
        const blacklistCollection = collection(db, 'blacklist');
        await setDoc(doc(blacklistCollection, user.phoneNumber), {
          blacklistedAt: new Date().toISOString(),
          reason: 'Manual blacklist by admin',
        });
        alert(`${user.phoneNumber} has been blacklisted.`);
        // Optionally, remove the user from the current view
        // Add the user to the blacklistData state to update the UI immediately
        const newBlacklistedUser = {
          phoneNumber: user.phoneNumber,
          blacklistedAt: new Date().toISOString(),
          reason: 'Manual blacklist by admin',
        };
        setBlacklistData([...blacklistData, newBlacklistedUser]);

        // Remove the user from the customerData state
        setCustomerData(customerData.filter(c => c.phoneNumber !== user.phoneNumber));
      } catch (error) {
        console.error("Error blacklisting user:", error);
        alert('Failed to blacklist user.');
      }
    }
  };

  const handleUnblacklistUser = async (phoneNumber) => {
    if (window.confirm(`Are you sure you want to unblock ${phoneNumber}?`)) {
      try {
        const blacklistRef = doc(db, 'blacklist', phoneNumber);
        await deleteDoc(blacklistRef);
        alert(`${phoneNumber} has been unblocked.`);
        setBlacklistData(blacklistData.filter(item => item.phoneNumber !== phoneNumber));
      } catch (error) {
        console.error("Error unblocking user:", error);
        alert('Failed to unblock user.');
      }
    }
  };

  const staffPhoneNumbers = {
    '+918553226547': 'Kaveri',
    '+918050959361': 'Mohana',
    '+918904511611': 'Sanjay',
    '+919742976141': 'Manjunath',
    '+917892139351': 'Peter',
    '+919880298802': 'Admin'
  };

  // Removed the unused error state

  const handleDashboardLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    
    try {
      setLoading(true);
      
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // If login is successful, set dashboard as authenticated
      if (userCredential.user) {
        setDashboardAuthenticated(true);
        fetchUserData(); // Call fetchUserData after successful authentication
      }
    } catch (error) {
      console.error("Dashboard authentication error:", error);
      setAuthError('Invalid email or password. Please try again.');
      setLoading(false);
    }
  };

  // In the fetchUserData function, ensure we're capturing the full timestamp
  const fetchUserData = async () => {
    try {
      setLoading(true);
      
      const loginsCollection = collection(db, 'user_logins');
      console.log("Attempting to fetch data from 'user_logins' collection");
      
      try {
        const loginsSnapshot = await getDocs(loginsCollection);
      
        if (loginsSnapshot.empty) {
          console.log("No data found in user_logins collection");
          setCustomerData([]);
          setStaffData([]);
        } else {
          const userMap = new Map();
          
          loginsSnapshot.docs.forEach(doc => {
            const data = doc.data();
            const phone = data.phoneNumber;
            
            if (!userMap.has(phone)) {
              userMap.set(phone, {
                phoneNumber: phone,
                logins: []
              });
            }
            
            // Add each login entry to the user's logins array
            // Check for MAC address in both places it might be stored
            const macAddress = data.macAddress || data.deviceInfo?.macAddress || 'unknown';
            
            userMap.get(phone).logins.push({
              lastLogin: data.lastLogin, // This contains the full date and time
              timestamp: data.timestamp || new Date(data.lastLogin).getTime(), // Use timestamp if available
              deviceInfo: {
                ...data.deviceInfo,
                // Ensure MAC address is available in deviceInfo
                macAddress: macAddress
              },
              // Also store at the login level for redundancy
              macAddress: macAddress
            });
          });
          
          // Convert map to array and sort by number of logins
          const userList = Array.from(userMap.values()).sort((a, b) => {
            return b.logins.length - a.logins.length;
          });
          
          // Sort each user's logins by timestamp in descending order
          userList.forEach(user => {
            user.logins.sort((a, b) => {
              const aTime = a.timestamp || new Date(a.lastLogin).getTime();
              const bTime = b.timestamp || new Date(b.lastLogin).getTime();
              return bTime - aTime; // Descending order
            });
          });
          
          const customers = userList.filter(user => !staffPhoneNumbers[user.phoneNumber]);
          const staff = userList.filter(user => staffPhoneNumbers[user.phoneNumber]);

          console.log("Fetched customer records:", customers.length);
          console.log("Fetched staff records:", staff.length);

          setCustomerData(customers);
          setStaffData(staff);

        const blacklistCollection = collection(db, 'blacklist');
        const blacklistSnapshot = await getDocs(blacklistCollection);
        const blacklistedUsers = blacklistSnapshot.docs.map(doc => ({ phoneNumber: doc.id, ...doc.data() }));
        setBlacklistData(blacklistedUsers);

        }
        setLoading(false);
      } catch (firestoreError) {
        console.error("Firestore error details:", firestoreError);
        setLoading(false);
      }
    } catch (err) {
      console.error("General error fetching data:", err);
      setLoading(false);
    }
  };

  const handleDashboardLogout = () => {
    // Simply set the dashboard authentication state to false
    setDashboardAuthenticated(false);
    setEmail('');
    setPassword('');
  };

  // Add filter state variables
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const parseDate = (dateString) => {
    if (typeof dateString === 'string') {
      const trimmed = dateString.trim();
      const match = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[ ,]+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);

      if (match) {
        const [, day, month, year, hour = '0', minute = '0', second = '0'] = match;
        return new Date(
          Number(year),
          Number(month) - 1,
          Number(day),
          Number(hour),
          Number(minute),
          Number(second)
        );
      }
    }
    return new Date(dateString);
  };

  const formatDateTime = (dateString) => {
    const date = parseDate(dateString);
    const datePart = date.toLocaleDateString('en-GB');
    const timePart = date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    return `${datePart}, ${timePart}`;
  };

  
  const resetFilters = () => {
    setStartDate(null);
    setEndDate(null);
  };
  
  const getFilteredCustomerData = () => {
    if (!startDate || !endDate) {
      return customerData;
    }

    // Set endDate to the end of the day to include all logins on that day
    const adjustedEndDate = new Date(endDate);
    adjustedEndDate.setHours(23, 59, 59, 999);

    return customerData.map(user => {
      const filteredLogins = user.logins.filter(login => {
        const loginDate = parseDate(login.lastLogin);
        return loginDate >= startDate && loginDate <= adjustedEndDate;
      });
      
      // Return user with filtered logins
      return {
        ...user,
        logins: filteredLogins
      };
    }).filter(user => user.logins.length > 0); // Only include users with matching logins
  };

  // Data processing for charts
  const chartData = useMemo(() => {
    const last7Days = [...Array(7)].map((_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - i);
      return d.toLocaleDateString('en-GB');
    }).reverse();

    const dailyLogins = last7Days.reduce((acc, day) => {
      acc[day] = new Set();
      return acc;
    }, {});

    const deviceTypes = {};
    const browserTypes = {};
    const repeatCustomers = new Map();

    customerData.forEach(user => {
      const dailyLoginDates = new Set();
      user.logins.forEach(login => {
        const loginDate = parseDate(login.lastLogin).toLocaleDateString('en-GB');

        // Daily unique logins
        if (dailyLogins[loginDate]) {
          dailyLogins[loginDate].add(user.phoneNumber);
        }

        // Device and browser types
        const device = login.deviceInfo?.deviceType || 'Unknown';
        const browser = login.deviceInfo?.os || 'Unknown'; // Changed from browser to os as per original code
        deviceTypes[device] = (deviceTypes[device] || 0) + 1;
        browserTypes[browser] = (browserTypes[browser] || 0) + 1;
        
        // Repeat customers (last 7 days)
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        if (parseDate(login.lastLogin) >= sevenDaysAgo) {
            dailyLoginDates.add(loginDate);
        }
      });
      
      if (dailyLoginDates.size > 1) {
          repeatCustomers.set(user.phoneNumber, dailyLoginDates.size);
      }
    });

    const barChartData = {
      labels: last7Days,
      datasets: [
        {
          label: 'Unique Logins',
          data: last7Days.map(day => dailyLogins[day]?.size || 0),
          backgroundColor: 'rgba(255, 255, 255, 0.5)',
        },
      ],
    };

    const devicePieChartData = {
        labels: Object.keys(deviceTypes),
        datasets: [{
            data: Object.values(deviceTypes),
            backgroundColor: ['rgba(255,255,255,0.7)', 'rgba(255,255,255,0.5)', 'rgba(255,255,255,0.3)'],
        }]
    };

    const browserPieChartData = {
        labels: Object.keys(browserTypes),
        datasets: [{
            data: Object.values(browserTypes),
            backgroundColor: ['rgba(255,255,255,0.7)', 'rgba(255,255,255,0.5)', 'rgba(255,255,255,0.3)'],
        }]
    };

    const last5Logins = customerData
      .flatMap(user => user.logins.map(login => ({ ...login, phoneNumber: user.phoneNumber })))
      .sort((a, b) => parseDate(b.lastLogin).getTime() - parseDate(a.lastLogin).getTime())
      .slice(0, 5);

    const barChartOptions = {
      plugins: {
        legend: {
          display: false,
        },
      },
    };

    return { barChartData, barChartOptions, devicePieChartData, browserPieChartData, last5Logins, repeatCustomers: Array.from(repeatCustomers.entries()) };
  }, [customerData]);

  // Update the day-wise logins table in the return statement
  const handleDownloadExcel = () => {
    const mapDataToSheet = (data, isStaff) => data.map(user => {
      const baseData = {
        'Name': user.name || (isStaff ? staffPhoneNumbers[user.phoneNumber] : 'N/A'),
        'Phone Number': user.phoneNumber,
        'Type': user.type || (isStaff ? 'Staff' : 'Customer'),
        'Number of Logins': user.logins.length,
        'Last Login': user.logins.map(login => formatDateTime(login.lastLogin)).join('\n'),
        'Device Info': user.logins.map(login => login.deviceInfo?.deviceType || 'N/A').join('\n'),
        'OS': user.logins.map(login => login.deviceInfo?.os || 'N/A').join('\n'),
        'MAC Address': user.logins.map(login => login.macAddress || login.deviceInfo?.macAddress || 'N/A').join('\n'),
      };
      return baseData;
    });

    const filteredCustomerData = getFilteredCustomerData();
    const customerSheet = XLSX.utils.json_to_sheet(mapDataToSheet(filteredCustomerData, false));
    const staffSheet = XLSX.utils.json_to_sheet(mapDataToSheet(staffData, true));

    // Add color to tabs
    customerSheet['!cols'] = [{ wch: 20 }, { wch: 20 }, { wch: 30 }, { wch: 20 }, { wch: 20 }, { wch: 20 }];
    staffSheet['!cols'] = [{ wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 30 }, { wch: 20 }, { wch: 20 }, { wch: 20 }];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, customerSheet, 'Customers');
    XLSX.utils.book_append_sheet(workbook, staffSheet, 'Staff');

    // It's not directly possible to color sheets with xlsx community edition.
    // This is a placeholder for a feature that would require a pro license or a different library.

    XLSX.writeFile(workbook, 'user_logins.xlsx');
  };

  const UserTable = ({ users, isStaffTable = false, onEditUser, onBlacklistUser }) => {
    // Add name and type to staff data from the hardcoded map for display
    const processedUsers = users.map(user => {
        if (isStaffTable && staffPhoneNumbers[user.phoneNumber] && !user.name) {
            return { ...user, name: staffPhoneNumbers[user.phoneNumber], type: 'Staff' };
        }
        return user;
    });

    const sortedUsers = [...processedUsers].sort((a, b) => {
      const aLatest = a.logins.length ? parseDate(a.logins[0].lastLogin).getTime() : 0;
      const bLatest = b.logins.length ? parseDate(b.logins[0].lastLogin).getTime() : 0;
      return bLatest - aLatest;
    });

    return (
        <table style={{ width: '100%' }}>
            <thead>
                <tr>
                    <th style={{ textAlign: 'center' }}>Name</th>
                    {!isStaffTable && <th style={{ textAlign: 'center' }}>Type</th>}
                    <th style={{ textAlign: 'center' }}>Phone Number</th>
                    <th style={{ textAlign: 'center' }}>Number of Logins</th>
                    <th style={{ textAlign: 'center' }}>Last Login</th>
                    <th style={{ textAlign: 'center' }}>Device Info</th>
                    <th style={{ textAlign: 'center' }}>OS</th>
                    <th style={{ textAlign: 'center' }}>MAC Address</th>
                </tr>
            </thead>
            <tbody>
                {sortedUsers.map(user => (
                    <tr key={user.phoneNumber}>
                        <td style={{ textAlign: 'center', padding: '8px' }}>{user.name || 'N/A'}</td>
                        {!isStaffTable && <td style={{ textAlign: 'center', padding: '8px' }}>{user.type || 'Customer'}</td>}
                        <td style={{ textAlign: 'center', padding: '8px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                                <span>{user.phoneNumber}</span>
                                {!isStaffTable && 
                                  <div style={{ marginTop: '5px' }}>
                                    <button className="edit-btn" onClick={() => onEditUser(user)}>Edit</button>
                                    <button className="blacklist-btn" onClick={() => onBlacklistUser(user)}>Blacklist</button>
                                  </div>
                                }
                            </div>
                        </td>
                        <td style={{ textAlign: 'center', padding: '8px' }}>{user.logins.length}</td>
                        <td style={{ textAlign: 'center', padding: '8px' }}>
                            {user.logins.map((login, index) => (
                                <div key={index} style={{ margin: '4px 0' }}>
                                    {formatDateTime(login.lastLogin)}
                                </div>
                            ))}
                        </td>
                        <td style={{ textAlign: 'center', padding: '8px' }}>
                            {user.logins.map((login, index) => (
                                <div key={index} style={{ margin: '4px 0' }}>
                                    {login.deviceInfo?.deviceType || 'N/A'}
                                </div>
                            ))}
                        </td>
                        <td style={{ textAlign: 'center', padding: '8px' }}>
                            {user.logins.map((login, index) => (
                                <div key={index} style={{ margin: '4px 0' }}>
                                    {login.deviceInfo?.os || 'N/A'}
                                </div>
                            ))}
                        </td>
                        <td style={{ textAlign: 'center', padding: '8px' }}>
                            {user.logins.map((login, index) => (
                                <div key={index} style={{ margin: '4px 0' }}>
                                    {login.macAddress || login.deviceInfo?.macAddress || 'N/A'}
                                </div>
                            ))}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

  const Card = ({ title, children }) => (
    <div className="card">
      <h3 className="card-title">{title}</h3>
      <div className="card-content">{children}</div>
    </div>
  );

  return (
    // Show login form if not authenticated for dashboard
    !dashboardAuthenticated ? (
      // Login form JSX with logo added
      <div className="dashboard-container">
        <div className="auth-container">
                    <div className="logo-container login-logo-container">
            <img src={logo} alt="Logo" className="logo" />
            <div className="shimmer-effect"></div>
          </div>
          <h2 style={{ color: 'var(--slate-text)' }}>Admin Dashboard</h2>
          {authError && <div className="auth-error">{authError}</div>}
          <form onSubmit={handleDashboardLogin} className="auth-form">
            <div className="form-group">
              <label htmlFor="dashboardEmail">Email</label>
              <input
                type="email"
                id="dashboardEmail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="dashboardPassword">Password</label>
              <input
                type="password"
                id="dashboardPassword"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="login-btn" disabled={loading}>
              {loading ? 'Logging in...' : 'Login to Dashboard'}
            </button>
          </form>
        </div>
      </div>
    ) : (
      // Dashboard content JSX with logo added to header
      <div className="dashboard-container">
        <header className="dashboard-header">
          <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div className="logo-container header-logo-container">
              <img src={logo} alt="Logo" className="logo" />
              <div className="shimmer-effect"></div>
            </div>
            <h1>User Access Dashboard</h1>
          </div>
          <div className="header-buttons">
            <button className="refresh-btn" onClick={fetchUserData}>Refresh Data</button>
            <button className="logout-btn" onClick={handleDashboardLogout}>Logout</button>
          </div>
        </header>

        <div className="cards-container">
          <Card title="Daily Users">
            <Bar data={chartData.barChartData} options={chartData.barChartOptions} />
          </Card>
          <Card title="Last 5 User">
            <ul className="last-users-list">
              {chartData.last5Logins.map((login, i) => (
                <li key={i}>{login.phoneNumber} - {formatDateTime(login.lastLogin)}</li>
              ))}
            </ul>
          </Card>
          <Card title={<>Repeat Customers<br/><small>(7 Days)</small></>}>
            <ul className="repeat-customers-list">
                {chartData.repeatCustomers.map(([phone, count]) => (
                    <li key={phone}>{phone} ({count} days)</li>
                ))}
            </ul>
          </Card>
          <Card title="Device Types">
            <Pie data={chartData.devicePieChartData} />
          </Card>
          <Card title="Browser Types">
            <Pie data={chartData.browserPieChartData} />
          </Card>
        </div>

        <div className="filter-accordion" style={{marginTop: '30px'}}>
          <div className="accordion-header" onClick={() => setShowFilters(!showFilters)}>
            <span>Filter by Date Range</span>
            <div style={{display: 'flex', alignItems: 'center'}}>
                <button className="download-btn" style={{marginRight: '10px'}} onClick={handleDownloadExcel}>Download Excel</button>
                <span>{showFilters ? '−' : '+'}</span>
            </div>
          </div>
          {showFilters && (
            <div className="filter-controls">
              <div className="date-picker-container">
                <DatePicker
                  selected={startDate}
                  onChange={(dates) => {
                    const [start, end] = dates;
                    setStartDate(start);
                    setEndDate(end);
                  }}
                  startDate={startDate}
                  endDate={endDate}
                  selectsRange
                  isClearable={true}
                  placeholderText="Select a date range"
                  dateFormat="dd/MM/yyyy"
                  className="date-picker-input"
                />
              </div>
              <button onClick={resetFilters} className="login-btn">Reset Filters</button>
            </div>
          )}
        </div>

        <div className="data-table" style={{ overflowX: 'auto' }}>
          <div className="collapsible-table-header" style={{cursor: 'pointer'}} onClick={() => setShowCustomerTable(!showCustomerTable)}>
            <h2 style={{ textAlign: 'center', margin: '0 auto', width: 'fit-content' }}>
              Customer Logins
              <span style={{ marginLeft: '10px' }}>
                {showCustomerTable ? '−' : '+'}
              </span>
            </h2>
          </div>
          {showCustomerTable && <UserTable users={getFilteredCustomerData()} onEditUser={setModalUser} onBlacklistUser={handleBlacklistUser} />}
        </div>

        <div className="data-table" style={{ overflowX: 'auto', marginTop: '30px' }}>
          <div className="collapsible-table-header" style={{cursor: 'pointer'}} onClick={() => setShowStaffTable(!showStaffTable)}>
            <h2 style={{ textAlign: 'center', margin: '0 auto', width: 'fit-content' }}>
              Staff Logins
              <span style={{ marginLeft: '10px' }}>
                {showStaffTable ? '−' : '+'}
              </span>
            </h2>
          </div>
          {showStaffTable && <UserTable users={staffData} isStaffTable={true} />}

        <div className="data-table" style={{ overflowX: 'auto', marginTop: '30px' }}>
          <div className="collapsible-table-header" style={{cursor: 'pointer'}} onClick={() => setShowBlacklistTable(!showBlacklistTable)}>
            <h2 style={{ textAlign: 'center', margin: '0 auto', width: 'fit-content' }}>
              Blacklisted Users
              <span style={{ marginLeft: '10px' }}>
                {showBlacklistTable ? '−' : '+'}
              </span>
            </h2>
          </div>
          {showBlacklistTable && <BlacklistTable users={blacklistData} onUnblacklistUser={handleUnblacklistUser} />}
        </div>
        </div>

        <CustomerTypeModal 
            user={modalUser} 
            onClose={() => setModalUser(null)} 
            onSave={handleSaveCustomerInfo} 
        />
      </div>
    )
  );
};

const CustomerTypeModal = ({ user, onClose, onSave }) => {
    const [type, setType] = useState('Customer');
    const [name, setName] = useState('');

    React.useEffect(() => {
        if (user) {
            // You can pre-fill this from your DB if you store it
            setType('Customer');
            setName('');
        }
    }, [user]);

    if (!user) return null;

    const handleSave = () => {
        onSave(user.phoneNumber, name, type);
    };

    return (
        <div className="modal-backdrop">
            <div className="modal">
                <h3>Edit User: {user.phoneNumber}</h3>
                <div className="form-group">
                    <label>Name</label>
                    <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Enter name" />
                </div>
                <div className="form-group">
                    <label>Customer Type</label>
                    <select value={type} onChange={e => setType(e.target.value)}>
                        <option value="Staff">Staff</option>
                        <option value="myHQ Customer">myHQ Customer</option>
                        <option value="Customer">Customer</option>
                    </select>
                </div>
                <div className="modal-actions">
                    <button className="login-btn" onClick={handleSave}>Save</button>
                    <button className="logout-btn" onClick={onClose}>Cancel</button>
                </div>
            </div>
        </div>
    );
};

const BlacklistTable = ({ users, onUnblacklistUser }) => {
  return (
    <table style={{ width: '100%' }}>
      <thead>
        <tr>
          <th style={{ textAlign: 'center' }}>Phone Number</th>
          <th style={{ textAlign: 'center' }}>Blacklisted At</th>
          <th style={{ textAlign: 'center' }}>Reason</th>
          <th style={{ textAlign: 'center' }}>Actions</th>
        </tr>
      </thead>
      <tbody>
        {users.map(user => (
          <tr key={user.phoneNumber}>
            <td style={{ textAlign: 'center', padding: '8px' }}>{user.phoneNumber}</td>
            <td style={{ textAlign: 'center', padding: '8px' }}>{new Date(user.blacklistedAt).toLocaleString('en-GB')}</td>
            <td style={{ textAlign: 'center', padding: '8px' }}>{user.reason}</td>
            <td style={{ textAlign: 'center', padding: '8px' }}>
              <button className="unblacklist-btn" onClick={() => onUnblacklistUser(user.phoneNumber)}>Unblock</button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default Dashboard;
